import { FormEvent, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  tenantApi,
  type ActivityEntry,
  type AuditEntry,
  type SettingsBundle,
  type Tenant,
} from '../../api/tenants';
import { TenantSelector } from '../../components/TenantSelector';

type Tab = 'overview' | 'business' | 'tax' | 'marketplace' | 'warehouse' | 'branding' | 'settings' | 'contacts' | 'addresses' | 'activity' | 'audit';

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const [searchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as Tab | null) ?? 'overview';
  const [tab, setTab] = useState<Tab>(initialTab);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [settings, setSettings] = useState<SettingsBundle | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cloneName, setCloneName] = useState('');

  async function loadTenant() {
    setLoading(true);
    setError('');
    try {
      setTenant(await tenantApi.get(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadTenant();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (tab === 'settings') void tenantApi.getSettings(id).then(setSettings).catch(() => setSettings(null));
    if (tab === 'activity') void tenantApi.getActivity(id).then(setActivity).catch(() => setActivity([]));
    if (tab === 'audit') void tenantApi.getAudit(id).then(setAudit).catch(() => setAudit([]));
  }, [id, tab]);

  async function lifecycle(action: 'activate' | 'suspend' | 'archive' | 'restore') {
    const fn = { activate: tenantApi.activate, suspend: tenantApi.suspend, archive: tenantApi.archive, restore: tenantApi.restore }[action];
    try {
      setTenant(await fn(id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    }
  }

  async function saveBusiness(e: FormEvent) {
    e.preventDefault();
    if (!tenant) return;
    setSaving(true);
    try {
      setTenant(await tenantApi.update(id, { business: tenant.business, profile: tenant.profile }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveSettings(e: FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setSaving(true);
    try {
      setSettings(await tenantApi.updateSettings(id, settings));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function onClone(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const cloned = await tenantApi.clone(id, { name: cloneName || `${tenant?.name} Copy` });
      window.location.href = `/tenants/${cloned.id}`;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Clone failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveTax(e: FormEvent) {
    e.preventDefault();
    if (!tenant) return;
    setSaving(true);
    try {
      setTenant(await tenantApi.update(id, { tax: tenant.tax }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveMarketplace(e: FormEvent) {
    e.preventDefault();
    if (!tenant) return;
    setSaving(true);
    try {
      setTenant(await tenantApi.update(id, { marketplace: tenant.marketplace }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveWarehouse(e: FormEvent) {
    e.preventDefault();
    if (!tenant) return;
    setSaving(true);
    try {
      setTenant(await tenantApi.update(id, { warehouse: tenant.warehouse }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function saveBranding(e: FormEvent) {
    e.preventDefault();
    if (!tenant) return;
    setSaving(true);
    try {
      setTenant(await tenantApi.update(id, { branding: tenant.branding }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'business', label: 'Business' },
    { key: 'tax', label: 'Tax' },
    { key: 'marketplace', label: 'Marketplace' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'branding', label: 'Branding' },
    { key: 'settings', label: 'Settings' },
    { key: 'contacts', label: 'Contacts' },
    { key: 'addresses', label: 'Addresses' },
    { key: 'activity', label: 'Activity' },
    { key: 'audit', label: 'Audit' },
  ];

  if (loading) return <p className="p-6 text-slate-600">Loading…</p>;
  if (!tenant) return <p className="p-6 text-red-600">{error || 'Tenant not found'}</p>;

  return (
    <div className="mx-auto max-w-5xl p-6">
      <Link to="/tenants" className="text-sm text-slate-600 hover:underline">← Tenants</Link>

      <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">{tenant.name}</h1>
          <p className="text-sm text-slate-600">{tenant.slug} · <span className="capitalize">{tenant.status}</span></p>
          <Link to={`/tenants/${id}/dashboard`} className="mt-1 inline-block text-sm text-slate-700 hover:underline">
            Open dashboard
          </Link>
        </div>
        <div className="flex flex-wrap gap-2">
          {tenant.status === 'pending' && (
            <button type="button" onClick={() => void lifecycle('activate')} className="rounded-lg border border-emerald-300 px-3 py-1.5 text-sm text-emerald-800">Activate</button>
          )}
          {tenant.status === 'active' && (
            <>
              <button type="button" onClick={() => void tenantApi.switch(id).then(loadTenant)} className="rounded-lg border border-blue-300 px-3 py-1.5 text-sm text-blue-800">Switch context</button>
              <button type="button" onClick={() => void lifecycle('suspend')} className="rounded-lg border border-amber-300 px-3 py-1.5 text-sm text-amber-800">Suspend</button>
            </>
          )}
          {(tenant.status === 'suspended' || tenant.status === 'archived') && (
            <button type="button" onClick={() => void lifecycle('restore')} className="rounded-lg border border-emerald-300 px-3 py-1.5 text-sm text-emerald-800">Restore</button>
          )}
          {tenant.status === 'active' && (
            <button type="button" onClick={() => void lifecycle('archive')} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm">Archive</button>
          )}
        </div>
      </div>

      <div className="mt-4 max-w-sm">
        <TenantSelector organizationId={tenant.organization_id} currentTenantId={tenant.id} />
      </div>

      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <nav className="mt-6 flex flex-wrap gap-1 border-b border-slate-200" aria-label="Tenant sections">
        {tabs.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm ${tab === t.key ? 'border-b-2 border-slate-900 font-medium' : 'text-slate-600'}`}>
            {t.label}
          </button>
        ))}
      </nav>

      <div className="mt-6">
        {tab === 'overview' && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card title="Identity">
              <dl className="space-y-2 text-sm">
                <Row label="ID" value={tenant.id} />
                <Row label="Organization" value={tenant.organization_id} />
                <Row label="Created" value={new Date(tenant.created_at).toLocaleString()} />
              </dl>
            </Card>
            <Card title="Regional">
              <dl className="space-y-2 text-sm">
                <Row label="Timezone" value={tenant.profile?.timezone ?? '—'} />
                <Row label="Currency" value={tenant.profile?.currency ?? '—'} />
                <Row label="Locale" value={tenant.profile?.locale ?? '—'} />
              </dl>
            </Card>
            <Card title="Clone tenant">
              <form onSubmit={onClone} className="flex gap-2">
                <input value={cloneName} onChange={(e) => setCloneName(e.target.value)} placeholder="Clone name" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm" />
                <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-3 py-2 text-sm text-white">Clone</button>
              </form>
            </Card>
          </div>
        )}

        {tab === 'business' && (
          <form onSubmit={saveBusiness} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field label="Legal name" value={tenant.business?.legal_name ?? ''} onChange={(v) => setTenant({ ...tenant, business: { ...tenant.business, legal_name: v } })} />
            <Field label="GST number" value={tenant.business?.gst_number ?? ''} onChange={(v) => setTenant({ ...tenant, business: { ...tenant.business, gst_number: v } })} />
            <Field label="PAN" value={tenant.business?.pan ?? ''} onChange={(v) => setTenant({ ...tenant, business: { ...tenant.business, pan: v } })} />
            <Field label="Business type" value={tenant.business?.business_type ?? ''} onChange={(v) => setTenant({ ...tenant, business: { ...tenant.business, business_type: v } })} />
            <Field label="Country" value={tenant.business?.country ?? ''} onChange={(v) => setTenant({ ...tenant, business: { ...tenant.business, country: v } })} />
            <Field label="State" value={tenant.business?.state ?? ''} onChange={(v) => setTenant({ ...tenant, business: { ...tenant.business, state: v } })} />
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white">{saving ? 'Saving…' : 'Save business info'}</button>
          </form>
        )}

        {tab === 'tax' && (
          <form onSubmit={saveTax} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={tenant.tax?.reverse_charge ?? false}
                onChange={(e) => setTenant({ ...tenant, tax: { ...tenant.tax, reverse_charge: e.target.checked } })}
              />
              Reverse charge applicable
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={tenant.tax?.composition_scheme ?? false}
                onChange={(e) => setTenant({ ...tenant, tax: { ...tenant.tax, composition_scheme: e.target.checked } })}
              />
              Composition scheme
            </label>
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white">
              {saving ? 'Saving…' : 'Save tax configuration'}
            </button>
          </form>
        )}

        {tab === 'marketplace' && (
          <form onSubmit={saveMarketplace} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field
              label="Default marketplace"
              value={tenant.marketplace?.default_marketplace ?? ''}
              onChange={(v) => setTenant({ ...tenant, marketplace: { ...tenant.marketplace, default_marketplace: v } })}
            />
            <Field
              label="Enabled marketplaces (comma-separated)"
              value={tenant.marketplace?.enabled_marketplaces?.join(', ') ?? ''}
              onChange={(v) =>
                setTenant({
                  ...tenant,
                  marketplace: {
                    ...tenant.marketplace,
                    enabled_marketplaces: v.split(',').map((s) => s.trim()).filter(Boolean),
                  },
                })
              }
            />
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white">
              {saving ? 'Saving…' : 'Save marketplace configuration'}
            </button>
          </form>
        )}

        {tab === 'warehouse' && (
          <form onSubmit={saveWarehouse} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field
              label="Default warehouse"
              value={tenant.warehouse?.default_warehouse ?? ''}
              onChange={(v) => setTenant({ ...tenant, warehouse: { ...tenant.warehouse, default_warehouse: v } })}
            />
            <Field
              label="Fulfillment model"
              value={tenant.warehouse?.fulfillment_model ?? ''}
              onChange={(v) => setTenant({ ...tenant, warehouse: { ...tenant.warehouse, fulfillment_model: v } })}
            />
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white">
              {saving ? 'Saving…' : 'Save warehouse configuration'}
            </button>
          </form>
        )}

        {tab === 'branding' && (
          <form onSubmit={saveBranding} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field label="Display name" value={tenant.branding?.display_name ?? ''} onChange={(v) => setTenant({ ...tenant, branding: { ...tenant.branding, display_name: v } })} />
            <Field label="Primary color" value={tenant.branding?.primary_color ?? ''} onChange={(v) => setTenant({ ...tenant, branding: { ...tenant.branding, primary_color: v } })} />
            <Field label="Logo URL" value={tenant.branding?.logo_url ?? ''} onChange={(v) => setTenant({ ...tenant, branding: { ...tenant.branding, logo_url: v } })} />
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white">{saving ? 'Saving…' : 'Save branding'}</button>
          </form>
        )}

        {tab === 'settings' && settings && (
          <form onSubmit={saveSettings} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
            <Field label="Setting key (theme)" value={settings.settings?.theme ?? ''} onChange={(v) => setSettings({ ...settings, settings: { ...settings.settings, theme: v } })} />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={settings.preferences?.notifications_enabled ?? false}
                onChange={(e) => setSettings({ ...settings, preferences: { ...settings.preferences, notifications_enabled: e.target.checked } })} />
              Notifications enabled
            </label>
            <button type="submit" disabled={saving} className="rounded-lg bg-slate-900 px-4 py-2 text-white">{saving ? 'Saving…' : 'Save settings'}</button>
          </form>
        )}

        {tab === 'contacts' && (
          <Card title="Contacts">
            {tenant.contacts?.length ? tenant.contacts.map((c) => (
              <p key={c.id} className="text-sm">{c.name} — {c.email || c.phone || '—'}</p>
            )) : <p className="text-sm text-slate-600">No contacts.</p>}
          </Card>
        )}

        {tab === 'addresses' && (
          <Card title="Addresses">
            {tenant.addresses?.length ? tenant.addresses.map((a) => (
              <p key={a.id} className="text-sm">{a.line1}, {a.city}, {a.country}</p>
            )) : <p className="text-sm text-slate-600">No addresses.</p>}
          </Card>
        )}

        {tab === 'activity' && (
          <Card title="Activity timeline">
            {activity.length === 0 ? <p className="text-sm text-slate-600">No activity.</p> : activity.map((a) => (
              <div key={a.id} className="border-l-2 border-slate-200 py-2 pl-4 text-sm">
                <p className="font-medium">{a.summary}</p>
                <p className="text-xs text-slate-500">{new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
          </Card>
        )}

        {tab === 'audit' && (
          <Card title="Audit log">
            {audit.length === 0 ? <p className="text-sm text-slate-600">No audit entries.</p> : audit.map((a) => (
              <div key={a.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                <p className="font-medium">{a.action}</p>
                <p className="text-slate-600">{a.outcome} · {new Date(a.created_at).toLocaleString()}</p>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return <div className="rounded-xl border border-slate-200 bg-white p-4"><h2 className="mb-3 font-medium">{title}</h2>{children}</div>;
}

function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between gap-4"><dt className="text-slate-500">{label}</dt><dd className="text-right">{value}</dd></div>;
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" />
    </label>
  );
}
